const LIVE = (row) => row?.status !== "CANCELLED";

const sum = (rows) =>
  rows.filter(LIVE).reduce((total, row) => total + (Number(row?.amount) || 0), 0);

/**
 * What an entry as a whole is doing, which is what the office acts on. Rows only ever differ
 * within an entry while a correction is working its way through approval.
 */
export const entryStatus = (rows) => {
  const live = (rows || []).filter(LIVE);
  if (live.length === 0) return (rows || []).length ? "CANCELLED" : "ACCRUED";
  const statuses = new Set(live.map((row) => row.status));
  if (statuses.size === 1) return live[0].status;
  if (statuses.has("PAID")) return "PART_PAID";
  if (statuses.has("APPROVED")) return "PART_APPROVED";
  return "ACCRUED";
};

const entryIdOf = (row) => row?.entryId ?? row?.id;

/**
 * Client, then the entry the money came from, then the rows that make it up. A source row and the
 * corrections made to it are one payable thing, so they are never shown as separate money.
 */
export const groupCommissions = (rows) => {
  const clients = new Map();

  (rows || []).forEach((row) => {
    const clientKey = row?.clientId ?? row?.clientName ?? "unknown";
    if (!clients.has(clientKey)) {
      clients.set(clientKey, {
        clientId: row?.clientId ?? null,
        clientName:
          row?.clientName || (row?.clientId ? `Client ${row.clientId}` : "Unknown client"),
        entries: new Map(),
      });
    }
    const client = clients.get(clientKey);
    const entryId = entryIdOf(row);
    if (!client.entries.has(entryId)) {
      client.entries.set(entryId, {
        entryId,
        moduleLabel: row?.moduleLabel || "Entry",
        rows: [],
      });
    }
    const entry = client.entries.get(entryId);
    entry.rows.push(row);
    if (!row?.correction && row?.moduleLabel) entry.moduleLabel = row.moduleLabel;
  });

  return [...clients.values()].map((client) => {
    const entries = [...client.entries.values()].map((entry) => {
      const ordered = [...entry.rows].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      return {
        ...entry,
        rows: ordered,
        net: sum(ordered),
        status: entryStatus(ordered),
        correctionCount: ordered.filter((row) => row.correction || row.entryId !== row.id).length,
      };
    });
    return {
      clientId: client.clientId,
      clientName: client.clientName,
      entries,
      net: entries.reduce((total, entry) => total + entry.net, 0),
      rowCount: entries.reduce((total, entry) => total + entry.rows.length, 0),
    };
  });
};

export const ENTRY_STATUS_LABELS = {
  ACCRUED: "Awaiting approval",
  APPROVED: "Approved",
  PAID: "Paid",
  CANCELLED: "Cancelled",
  PART_APPROVED: "Correction pending",
  PART_PAID: "Partly paid",
};

/** What a row is within its entry: the amount first entered, then each correction to it. */
export const rowRole = (row, index) => {
  if (index === 0) return "Original amount";
  return (Number(row?.amount) || 0) < 0 ? "Correction, reduced" : "Correction, increased";
};
